import type { Centrifuge, Subscription, PublicationContext, SubscribedContext } from 'centrifuge'
import { getQueryKey } from '@trpc/react-query'
import { wsClient } from './websocket'
import { queryClient } from './query-client'
import { trpc } from './trpc'
import { dbgEvent } from './debug'
import { useAuthStore } from '../stores/auth.store'
import { useTabsStore } from '../stores/tabs.store'
import type { TerminalEventWithSeq } from '../../../../shared/types/terminal-event.js'
import { messagesHandler } from '../channels/messages.handler'
import { statusHandler } from '../channels/status.handler'
import { terminalHandler } from '../channels/terminal.handler'
import { metricsHandler } from '../channels/metrics.handler'
import { tasksHandler } from '../channels/tasks.handler'
import { backgroundTasksHandler } from '../channels/background-tasks.handler'
import { sessionHandler } from '../channels/session.handler'

/**
 * Gestionnaire de souscriptions Centrifugo (swap étape 1b, contrat #10993).
 *
 * Porte la TABLE DE ROUTAGE event→handler (ex-`dispatch` de websocket.ts), redistribuée
 * sur les 5 canaux typés. Le `sid` est connu à la création de chaque souscription (closure) :
 * plus de regex `agent:(.+)` pour l'extraire.
 *
 * Canaux (user-limited, suffixe `#{userId}` — le serveur publie sur `<canal>:{sid}#{uid}`
 * et `session:user:{uid}#{uid}`, envelope `{event, data}`) :
 *  - permanent : `session:user:{uid}#{uid}` → sessionHandler.onSessionEvent(event, data)
 *  - par onglet conversation ouvert : `messages:{sid}#{uid}`, `status:{sid}#{uid}`,
 *    `terminal:{sid}#{uid}` (PAS `metrics` : dérivé client-side du terminal, comme aujourd'hui).
 *
 * Réconciliation dynamique : on suit les onglets `type==='conversation'` via
 * `useTabsStore.subscribe` (impératif, pas de sélecteur/composant — pattern E5 anti-fuite),
 * et on diffe le Set des sessionId d'onglets contre une `Map<sid, Subscription[]>`.
 */

type Envelope = { event: string; data: Record<string, unknown> }

/** Type local metrics (vue partielle sur le TerminalEvent), miroir de l'ancien websocket.ts. */
type MetricsEvent =
  | { type: 'system_init'; model: string; [key: string]: unknown }
  | { type: 'result'; totalCostUsd: number; [key: string]: unknown }

function userId(): string | null {
  return useAuthStore.getState().user?.id ?? null
}

/**
 * Recovery — filet de sécurité par souscription recoverable.
 * Centrifugo suit offset+epoch (config serveur). Si une reconnexion a tenté un recovery
 * qui a ÉCHOUÉ (`wasRecovering && !recovered`), on invalide les caches React Query de la
 * session pour refetch la vérité BDD (réutilise le chemin d'invalidation existant).
 */
function invalidateSessionQueries(sid: string): void {
  const messagesKey = getQueryKey(trpc.messages.list, { sessionId: sid }, 'query')
  void queryClient.invalidateQueries({ queryKey: messagesKey })
  const terminalKey = getQueryKey(trpc.messages.terminalEvents.listBySession, { sessionId: sid }, 'query')
  void queryClient.invalidateQueries({ queryKey: terminalKey })
}

function onRecoveryFailed(sid: string) {
  return (ctx: SubscribedContext) => {
    if (ctx.wasRecovering && !ctx.recovered) {
      dbgEvent('WS', `recovery failed sid=${sid.slice(0, 8)}... → invalidate RQ`, { channel: ctx.channel })
      invalidateSessionQueries(sid)
    }
  }
}

class SubscriptionManager {
  private started = false
  private unsubTabs: (() => void) | null = null
  private sessionSub: Subscription | null = null
  /** Par sid : les 3 souscriptions de canal (messages, status, terminal). */
  private sessionChannels = new Map<string, Subscription[]>()

  /** Démarre la gestion : canal permanent + réconciliation onglets. Idempotent. */
  start(): void {
    if (this.started) return
    const uid = userId()
    if (!uid) {
      console.warn('[subscriptions] no userId — cannot start subscription manager')
      return
    }
    this.started = true
    const client = wsClient.getClient()

    this.subscribeSessionChannel(client, uid)

    // Réconciliation immédiate sur l'état courant (onglets restaurés du localStorage)…
    this.reconcile(client, uid)
    // …puis sur chaque changement d'onglets (impératif — pattern E5, aucun re-render).
    this.unsubTabs = useTabsStore.subscribe(() => this.reconcile(client, uid))
  }

  /** Arrête tout : désabonne les onglets, retire toutes les souscriptions. */
  stop(): void {
    if (!this.started) return
    this.started = false

    if (this.unsubTabs) {
      this.unsubTabs()
      this.unsubTabs = null
    }

    const client = wsClient.getClient()

    for (const [, subs] of this.sessionChannels) {
      for (const sub of subs) {
        sub.unsubscribe()
        client.removeSubscription(sub)
      }
    }
    this.sessionChannels.clear()

    if (this.sessionSub) {
      this.sessionSub.unsubscribe()
      client.removeSubscription(this.sessionSub)
      this.sessionSub = null
    }
  }

  /** Diffe le Set des sessionId d'onglets conversation ouverts contre les souscriptions actives. */
  private reconcile(client: Centrifuge, uid: string): void {
    const desired = new Set(
      useTabsStore
        .getState()
        .tabs.filter((t) => t.type === 'conversation' && t.sessionId)
        .map((t) => t.sessionId as string),
    )

    // Onglets ouverts non encore souscrits → souscrire.
    for (const sid of desired) {
      if (!this.sessionChannels.has(sid)) {
        this.subscribeSession(client, uid, sid)
      }
    }
    // Souscriptions dont l'onglet a été fermé → retirer.
    for (const sid of [...this.sessionChannels.keys()]) {
      if (!desired.has(sid)) {
        this.unsubscribeSession(client, sid)
      }
    }
  }

  /** Canal permanent `session:user:{uid}#{uid}` → sessionHandler.onSessionEvent(event, data). */
  private subscribeSessionChannel(client: Centrifuge, uid: string): void {
    const channel = `session:user:${uid}#${uid}`
    this.sessionSub = this.makeSub(client, channel, (env) => {
      dbgEvent('WS', `← ${channel} : ${env.event}`, env.data)
      try {
        sessionHandler.onSessionEvent(env.event, env.data)
      } catch (e) {
        console.error('[subscriptions] session.onSessionEvent error', e)
      }
    })
  }

  /** Souscrit les 3 canaux d'une session conversation ouverte. */
  private subscribeSession(client: Centrifuge, uid: string, sid: string): void {
    const messages = this.makeSub(client, `messages:${sid}#${uid}`, (env) => {
      dbgEvent('WS', `← messages:${sid.slice(0, 8)} : ${env.event}`, env.data)
      this.routeMessages(sid, env)
    })
    messages.on('subscribed', onRecoveryFailed(sid))

    const status = this.makeSub(client, `status:${sid}#${uid}`, (env) => {
      dbgEvent('WS', `← status:${sid.slice(0, 8)} : ${env.event}`, env.data)
      this.routeStatus(sid, env)
    })

    const terminal = this.makeSub(client, `terminal:${sid}#${uid}`, (env) => {
      dbgEvent('WS', `← terminal:${sid.slice(0, 8)} : ${env.event}`, env.data)
      this.routeTerminal(sid, env)
    })
    terminal.on('subscribed', onRecoveryFailed(sid))

    this.sessionChannels.set(sid, [messages, status, terminal])
  }

  /** Retire les 3 canaux d'une session (onglet fermé). */
  private unsubscribeSession(client: Centrifuge, sid: string): void {
    const subs = this.sessionChannels.get(sid)
    if (!subs) return
    for (const sub of subs) {
      sub.unsubscribe()
      client.removeSubscription(sub)
    }
    this.sessionChannels.delete(sid)
  }

  /**
   * Crée une souscription et câble les publications. Garde contre le doublon
   * (Centrifuge.newSubscription throw si le canal est déjà enregistré).
   */
  private makeSub(client: Centrifuge, channel: string, onEnvelope: (env: Envelope) => void): Subscription {
    const existing = client.getSubscription(channel)
    if (existing) return existing
    const sub = client.newSubscription(channel)
    sub.on('publication', (ctx: PublicationContext) => {
      onEnvelope(ctx.data as Envelope)
    })
    sub.subscribe()
    return sub
  }

  // --- Table de routage event→handler (ex-dispatch de websocket.ts) ---

  /** messages:{sid} — partial / complete / error → messagesHandler. */
  private routeMessages(sid: string, env: Envelope): void {
    if (env.event === 'partial') {
      try { messagesHandler.onPartial(sid, env.data as { content: string }) }
      catch (e) { console.error('[subscriptions] messages.onPartial error', e) }
    } else if (env.event === 'complete') {
      try { messagesHandler.onComplete(sid, env.data as { messageId: string; content: string }) }
      catch (e) { console.error('[subscriptions] messages.onComplete error', e) }
    } else if (env.event === 'error') {
      try { messagesHandler.onError(sid, env.data as { error: string; partialContent?: string }) }
      catch (e) { console.error('[subscriptions] messages.onError error', e) }
    }
  }

  /**
   * status:{sid} — changed → statusHandler.onStatusChanged ET terminalHandler.onStatusReset.
   * Couplage terminal↔status OBLIGATOIRE (contrat #10993) : le canal status déclenche le reset
   * des liveEvents du terminal (idle/error/closed).
   */
  private routeStatus(sid: string, env: Envelope): void {
    if (env.event !== 'changed') return
    // error? préservé (journal-minidev-tout-afficher-terminal-pulsar-2026-08-11) : le serveur
    // émet parfois status:changed avec un champ error (ex. sessions.ts createSession catch) —
    // l'ancien cast `{ status: string }` l'effaçait avant qu'aucun handler ne puisse le lire.
    const data = env.data as { status: string; error?: string }
    try { statusHandler.onStatusChanged(sid, data) }
    catch (e) { console.error('[subscriptions] status.onStatusChanged error', e) }
    try { terminalHandler.onStatusReset(sid, data) }
    catch (e) { console.error('[subscriptions] terminal.onStatusReset error', e) }
  }

  /**
   * terminal:{sid} — TROIS noms d'event partagent ce canal (visibilite Cowboys #31513) :
   *  - 'terminal' → terminalHandler.onTerminalEvent (regime A), PUIS dérivation metrics
   *    client-side conservée (system_init/result → metricsHandler ; le serveur ne publie PAS de
   *    canal metrics, il est dérivé du terminal, comme aujourd'hui).
   *  - 'task_progress' → tasksHandler UNIQUEMENT (regime B ephemere).
   *  - 'background_tasks' → backgroundTasksHandler UNIQUEMENT (regime B ephemere).
   * Les deux events regime B NE VONT NULLE PART AILLEURS : surtout PAS terminalHandler/liveEvents.
   * Un event sans seq n'y serait jamais confirme donc jamais elague → fuite memoire. C'est la
   * condition qui rend le regime ephemere sur (brief §4bis).
   */
  private routeTerminal(sid: string, env: Envelope): void {
    if (env.event === 'task_progress') {
      try { tasksHandler.onTaskProgress(sid, env.data as { taskId: string; [key: string]: unknown }) }
      catch (e) { console.error('[subscriptions] tasks.onTaskProgress error', e) }
      return // INVARIANT : une progression n'atteint jamais liveEvents/terminalHandler.
    }
    if (env.event === 'background_tasks') {
      try { backgroundTasksHandler.onBackgroundTasks(sid, env.data as { tasks?: { task_id: string }[]; [key: string]: unknown }) }
      catch (e) { console.error('[subscriptions] tasks.onBackgroundTasks error', e) }
      return // INVARIANT : la reconciliation n'atteint jamais liveEvents/terminalHandler.
    }
    if (env.event !== 'terminal') return
    const data = env.data as { type: string; [key: string]: unknown }
    try { terminalHandler.onTerminalEvent(sid, data as TerminalEventWithSeq) }
    catch (e) { console.error('[subscriptions] terminal.onTerminalEvent error', e) }
    if (data.type === 'system_init' || data.type === 'result') {
      try { metricsHandler.onMetricsEvent(sid, data as MetricsEvent) }
      catch (e) { console.error('[subscriptions] metrics.onMetricsEvent error', e) }
    }
  }
}

export const subscriptionManager = new SubscriptionManager()
